import React from 'react';
import { Row, Col, Card } from 'antd';
import {
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  ComposedChart, Bar, Line
} from 'recharts';

export default function MonteCarloCharts({ samples }) {
  if (!samples) return null;

  // Prepare histogram-like data via simple binning
  const buildHist = (arr, bins = 20) => {
    if (!arr || arr.length === 0) return [];
    const vals = [...arr].sort((a,b)=>a-b);
    const min = vals[0];
    const max = vals[vals.length-1];
    const width = (max - min) / (bins || 1) || 1;
    const data = Array.from({ length: bins }, (_, i) => ({
      name: `${Math.round(min + i*width).toLocaleString()}-${Math.round(min + (i+1)*width).toLocaleString()}`,
      count: 0
    }));
    vals.forEach(v => {
      let idx = Math.floor((v - min) / width);
      if (idx >= bins) idx = bins - 1;
      if (idx < 0) idx = 0;
      data[idx].count += 1;
    });
    return data;
  };

  // Quartiles preparation
  const quartiles = (arr, name) => {
    if (!arr || arr.length === 0) return null;
    const sorted = [...arr].sort((a, b) => a - b);
    const n = sorted.length;
    const q = (p) => sorted[Math.max(0, Math.min(n - 1, Math.round(p * (n - 1))))];
    return {
      name,
      min: sorted[0],
      q1: q(0.25),
      median: q(0.5),
      q3: q(0.75),
      max: sorted[n - 1]
    };
  };

  const baseData = [
    quartiles(samples.total, 'Total'),
    quartiles(samples.risky, 'Risky'),
    quartiles(samples.medium, 'Medium'),
    quartiles(samples.safe, 'Safe')
  ].filter(Boolean);

  const aprData = [
    samples?.apr?.risky ? quartiles(samples.apr.risky, 'APR Risky') : null,
    samples?.apr?.medium ? quartiles(samples.apr.medium, 'APR Medium') : null,
    samples?.apr?.safe ? quartiles(samples.apr.safe, 'APR Safe') : null,
  ].filter(Boolean);

  // Transform for stacked bar (transparent base at q1, visible IQR = q3-q1)
  const toIqrStack = (rows) => rows.map(r => ({
    name: r.name,
    base: r.q1,
    iqr: Math.max(0, r.q3 - r.q1),
    median: r.median,
    min: r.min,
    max: r.max,
  }));

  const iqrData = toIqrStack(baseData);
  const aprIqrData = toIqrStack(aprData);

  const BoxPlot = ({ yScale, offset, data, color = '#3182ce' }) => {
    if (!data || data.length === 0) return null;
    return (
      <g>
        {data.map((d, idx) => {
          if (!yScale || !offset) return null;
          const band = Math.max(1, (offset.width || 1) / Math.max(1, data.length));
          const cx = (offset.left || 0) + band * (idx + 0.5);
          const bw = Math.max(12, Math.min(40, band * 0.6));

          const yMin = yScale(d.min);
          const yQ1 = yScale(d.q1);
          const yMed = yScale(d.median);
          const yQ3 = yScale(d.q3);
          const yMax = yScale(d.max);

          const allFinite = [yMin, yQ1, yMed, yQ3, yMax].every(v => Number.isFinite(v));
          if (!allFinite) return null;

          const boxY = Math.min(yQ1, yQ3);
          const boxH = Math.max(1, Math.abs(yQ1 - yQ3));
          return (
            <g key={`box-${idx}`}>
              {/* Whiskers */}
              <line x1={cx} x2={cx} y1={yMax} y2={yQ3} stroke={color} strokeWidth={3} />
              <line x1={cx - bw/2} x2={cx + bw/2} y1={yMax} y2={yMax} stroke={color} strokeWidth={3} />
              <line x1={cx} x2={cx} y1={yQ1} y2={yMin} stroke={color} strokeWidth={3} />
              <line x1={cx - bw/2} x2={cx + bw/2} y1={yMin} y2={yMin} stroke={color} strokeWidth={3} />
              {/* IQR box */}
              <rect x={cx - bw/2} y={boxY} width={bw} height={boxH} fill={color} opacity={0.2} stroke={color} />
              {/* Median */}
              <line x1={cx - bw/2} x2={cx + bw/2} y1={yMed} y2={yMed} stroke={color} strokeWidth={2} />
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card title="Pool Balances Quartile Bands (IQR) + Median">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={iqrData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value, name) => [typeof value === 'number' ? value.toLocaleString() : value, name]} />
              <Bar dataKey="base" stackId="a" fill="transparent" stroke="transparent" />
              <Bar dataKey="iqr" stackId="a" fill="#90cdf4" name="IQR (Q1..Q3)" />
              <Line dataKey="median" type="monotone" stroke="#3182ce" dot={false} name="Median" />
              <Line dataKey="min" type="monotone" stroke="#e53e3e" strokeWidth={2} dot={false} name="Min" />
              <Line dataKey="max" type="monotone" stroke="#e53e3e" strokeWidth={2} dot={false} name="Max" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </Col>
      <Col span={24}>
        <Card title="APR Quartile Bands (IQR) + Median (%)">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={aprIqrData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value, name) => [typeof value === 'number' ? value.toFixed(2) : value, name]} />
              <Bar dataKey="base" stackId="a" fill="transparent" stroke="transparent" />
              <Bar dataKey="iqr" stackId="a" fill="#d6bcfa" name="IQR (Q1..Q3)" />
              <Line dataKey="median" type="monotone" stroke="#6a4c93" dot={false} name="Median" />
              <Line dataKey="min" type="monotone" stroke="#e53e3e" strokeWidth={2} dot={false} name="Min" />
              <Line dataKey="max" type="monotone" stroke="#e53e3e" strokeWidth={2} dot={false} name="Max" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      </Col>
    </Row>
  );
}


